import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { motion } from 'framer-motion';
import { SparklesIcon, MapPinIcon, BoltIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { GET_NEW_ADS } from '../../graphql/queries';
import { getAbsoluteImageUrl } from '../../lib/imageUtils';

interface InfiniteScrollSectionProps {
  title?: string;
  limit?: number;
}

const InfiniteScrollSection: React.FC<InfiniteScrollSectionProps> = ({
  title = 'Fresh Recommendations',
  limit = 10,
}) => {
  const navigate = useNavigate();
  const [ads, setAds] = useState<any[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);

  const { fetchMore } = useQuery(GET_NEW_ADS, {
    variables: { limit, offset: 0 },
    onCompleted: (data) => {
      if (data?.newAds) {
        setAds(data.newAds);
        setHasMore(data.newAds.length === limit);
      }
      setInitialLoading(false);
    },
    onError: () => {
      setInitialLoading(false);
    },
  });

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const result = await fetchMore({
        variables: { limit, offset: offset + limit },
      });

      if (result.data?.newAds) {
        const newAds = result.data.newAds;
        setAds((prev) => [...prev, ...newAds]);
        setOffset((prev) => prev + limit);
        setHasMore(newAds.length === limit);
      }
    } catch (error) {
      console.error('Error loading more ads:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, offset, limit, fetchMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadMore]);

  const formatPrice = (price: string | null) => {
    if (!price) return 'Free';
    const numPrice = parseFloat(price);
    if (!isNaN(numPrice)) {
      return Math.round(numPrice).toLocaleString('en-NP');
    }
    return price;
  };

  return (
    <div className="px-3 py-4" style={{ background: 'var(--pw-bg)' }}>
      {/* Section Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'rgba(74, 132, 217, 0.1)',
              color: 'var(--pw-info)',
            }}
          >
            <SparklesIcon style={{ width: 20, height: 20 }} />
          </div>
          <h3 className="fw-bold mb-0" style={{ fontSize: 18, color: 'var(--pw-text)' }}>
            {title}
          </h3>
        </div>
        <span style={{ fontSize: 13, color: 'var(--pw-text-muted)', fontWeight: 500 }}>
          {ads.length} items
        </span>
      </div>

      {/* Ads Grid */}
      {ads.length === 0 && !loading && !initialLoading ? (
        <div className="text-center py-4">
          <p className="mb-0" style={{ fontSize: 14, color: 'var(--pw-text-muted)' }}>
            No ads at the moment
          </p>
        </div>
      ) : (
        <div className="row g-3">
          {ads.map((ad: any, index: number) => (
            <div key={`${ad.id}-${index}`} className="col-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.03, 0.3), duration: 0.4 }}
                onClick={() => navigate(`/ad/${ad.slug}`)}
                className="cursor-pointer h-100"
                style={{
                  background: 'var(--pw-surface)',
                  border: '1px solid var(--pw-border)',
                  borderRadius: 0,
                  overflow: 'hidden',
                  boxShadow: '0 4px 14px rgba(116, 67, 35, 0.05)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 10px 24px rgba(116, 67, 35, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(116, 67, 35, 0.05)';
                }}
              >
                {/* Image */}
                <div className="position-relative bg-light" style={{ aspectRatio: '1/1', overflow: 'hidden' }}>
                  {ad.primaryImage ? (
                    <img
                      src={getAbsoluteImageUrl(ad.primaryImage) || '/placeholder-ad.png'}
                      alt={ad.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: '100%',
                        height: '100%',
                        background: 'var(--pw-bg-soft)',
                      }}
                    >
                      <svg
                        style={{ width: 40, height: 40, color: 'var(--pw-text-muted)' }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}

                  {/* Sponsored Badge */}
                  {ad.isFeatured && (
                    <div
                      className="position-absolute d-flex align-items-center gap-1"
                      style={{
                        top: 8,
                        right: 8,
                        padding: '4px 10px',
                        borderRadius: 999,
                        background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-strong) 100%)',
                        color: 'white',
                        fontSize: 10,
                        fontWeight: 700,
                        boxShadow: '0 4px 10px rgba(232, 93, 76, 0.3)',
                      }}
                    >
                      <BoltIcon style={{ width: 10, height: 10 }} />
                      <span>Ad</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-3">
                  <h4
                    className="fw-semibold mb-2"
                    style={{
                      fontSize: 14,
                      color: 'var(--pw-text)',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.4,
                      minHeight: 40,
                    }}
                  >
                    {ad.title}
                  </h4>

                  <div className="d-flex align-items-baseline gap-1 mb-2">
                    <span
                      className="fw-bold"
                      style={{ fontSize: 15, color: 'var(--pw-primary-strong)' }}
                    >
                      Rs. {formatPrice(ad.price)}
                    </span>
                    {ad.priceType === 'negotiable' && (
                      <span style={{ fontSize: 11, color: 'var(--pw-text-muted)' }}>(Neg)</span>
                    )}
                  </div>

                  <div
                    className="d-flex align-items-center gap-1"
                    style={{ fontSize: 12, color: 'var(--pw-text-muted)' }}
                  >
                    <MapPinIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
                    <span className="text-truncate">{ad.city}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      )}

      {/* Initial Loading Skeleton */}
      {initialLoading && (
        <div className="row g-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="col-6">
              <div
                style={{
                  background: 'var(--pw-surface)',
                  border: '1px solid var(--pw-border)',
                  borderRadius: 0,
                  overflow: 'hidden',
                }}
              >
                <div className="skeleton" style={{ aspectRatio: '1/1' }} />
                <div className="p-3">
                  <div className="skeleton mb-2" style={{ height: 16, width: '70%', borderRadius: 6 }} />
                  <div className="skeleton mb-2" style={{ height: 14, width: '50%', borderRadius: 6 }} />
                  <div className="skeleton" style={{ height: 12, width: '40%', borderRadius: 4 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load More Button */}
      {!initialLoading && hasMore && !loading && (
        <div className="d-flex justify-content-center pt-4">
          <button
            onClick={loadMore}
            className="d-flex align-items-center gap-2 border-0 fw-semibold"
            style={{
              padding: '12px 24px',
              borderRadius: 12,
              background: 'var(--pw-surface)',
              borderWidth: '1.5px',
              borderStyle: 'solid',
              borderColor: 'var(--pw-border)',
              color: 'var(--pw-text)',
              fontSize: 14,
              boxShadow: '0 4px 14px rgba(116, 67, 35, 0.06)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--pw-bg-soft)';
              e.currentTarget.style.borderColor = '#f2b58f';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 22px rgba(116, 67, 35, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--pw-surface)';
              e.currentTarget.style.borderColor = 'var(--pw-border)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 14px rgba(116, 67, 35, 0.06)';
            }}
          >
            <ArrowDownIcon style={{ width: 16, height: 16 }} />
            Load More
          </button>
        </div>
      )}

      {/* Loading Spinner at Bottom */}
      {loading && !initialLoading && (
        <div className="d-flex justify-content-center py-4">
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: '3px solid var(--pw-border)',
              borderTopColor: 'var(--pw-primary)',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* End of List Message */}
      {!hasMore && ads.length > 0 && !initialLoading && (
        <div className="text-center py-4">
          <p className="mb-0" style={{ fontSize: 14, color: 'var(--pw-text-muted)' }}>
            You&apos;ve seen all the ads
          </p>
        </div>
      )}

      {/* Observer Target for automatic infinite scroll */}
      {hasMore && !initialLoading && (
        <div ref={observerTarget} style={{ height: 40 }} />
      )}
    </div>
  );
};

export default InfiniteScrollSection;
